import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card } from "./ui";
import { TrendingUp, ArrowUpRight } from "lucide-react";

const fmt = (v, loc) => new Intl.NumberFormat(loc, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

// Cross-entity sales pipeline rollup for the Group HQ (open vs won value per entity).
// Hidden for scoped users (403) and when there are no deals.
export default function SalesPipelineWidget() {
  const { t, lang, entities } = useApp();
  const loc = lang === "fr" ? "fr-FR" : "en-US";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(() => {
    api.get("/reports/sales")
      .then(({ data }) => setData(data))
      .catch(() => setHidden(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (hidden || !data) return null;
  if (!data.entities.length) return null;

  const totalPipeline = data.totals.open_value + data.totals.won_value;
  const gotoEntity = (eid) => { const e = entities.find((x) => x.id === eid); if (e) navigate(`/entity/${e.key}?tab=deals`); };

  return (
    <Card className="mb-6" data-testid="sales-pipeline-widget">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-text-secondary" />
          <h3 className="font-heading font-medium">{t("sales_title")}</h3>
        </div>
        <div className="flex items-center gap-4 text-xs text-text-secondary">
          <span>{t("deals_open_value")} <span className="font-medium text-text-primary tabular-nums">{fmt(data.totals.open_value, loc)}</span></span>
          <span>{t("deals_won_value")} <span className="font-medium text-emerald-500 tabular-nums">{fmt(data.totals.won_value, loc)}</span></span>
        </div>
      </div>
      <div className="space-y-4">
        {data.entities.map((r) => {
          const total = r.open_value + r.won_value;
          const wonPct = total ? (r.won_value / total) * 100 : 0;
          return (
            <button key={r.entity_id} onClick={() => gotoEntity(r.entity_id)} data-testid={`sales-row-${r.entity_id}`} className="w-full text-left group">
              <div className="flex items-center justify-between mb-1.5">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: r.color }} />
                  {r.name}
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-text-secondary" />
                </span>
                <span className="flex items-center gap-3 text-xs tabular-nums text-text-secondary">
                  <span>{r.open_count + r.won_count} {t("sales_deals")}</span>
                  <span className="font-medium text-text-primary">{fmt(total, loc)}</span>
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden flex" style={{ background: "var(--muted)" }}>
                <div className="h-full bg-emerald-400 transition-all duration-500" style={{ width: `${wonPct}%` }} title={`${t("deals_won_value")} ${fmt(r.won_value, loc)}`} />
                <div className="h-full transition-all duration-500" style={{ width: `${100 - wonPct}%`, backgroundColor: r.color, opacity: 0.55 }} title={`${t("deals_open_value")} ${fmt(r.open_value, loc)}`} />
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
